import { useState } from 'react';
import { AppExceptionResponse } from '../../../hooks/useHandleAppException';
import LangEnum from '../../../lang/LangEnum';
import api from '../../../services/api';
import snackbarStore from '../../../stores/Snackbar.store';
import useOAuthErrorMessage from './useOAuthErrorMessage';

interface UseOAuthConsentReturn {
  handleConsent: () => Promise<void>;
  handleDeny: () => Promise<void>;
  isSubmitting: boolean;
  errorMessage: string | null;
}

const useOAuthConsent = (oauthRequestId: string): UseOAuthConsentReturn => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const resolveErrorMessage = useOAuthErrorMessage();

  const submitConsent = async (isGranted: boolean) => {
    setIsSubmitting(true);

    const response = await api.oauthConsent({
      oauth_request_id: oauthRequestId,
      granted: isGranted,
    });

    if (response.ok && response.data?.redirect_to) {
      window.location.href = response.data.redirect_to;
      return;
    }

    const exceptionResponse = response as unknown as AppExceptionResponse;
    const message = resolveErrorMessage(exceptionResponse);

    const code = exceptionResponse.data?.code;

    if (
      code === LangEnum.API_EXCEPTION_OAUTH_CONSENT_BINDING_INVALID ||
      code === LangEnum.API_EXCEPTION_OAUTH_REQUEST_EXPIRED
    ) {
      setErrorMessage(message);
      return;
    }

    snackbarStore.handleMessage({ message, type: 'error' });
    setIsSubmitting(false);
  };

  const handleConsent = () => submitConsent(true);
  const handleDeny = () => submitConsent(false);

  return { handleConsent, handleDeny, isSubmitting, errorMessage };
};

export default useOAuthConsent;
